import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { request } from './client'

/** Инструменты: разбор по ссылкам, «Скачать Reels», «Скачать с YouTube», ролики в «Добавленные».
 *  Контракт — `backend/src/cf2/api/tools.py` и `api/manual.py`. Задачу делает воркер, фронт спрашивает статус. */

/** feed_add — ролики в «Добавленные» Залётов, feed_refresh — «Обновить цифры» в карточке. */
export type JobKind = 'analyze' | 'reel' | 'youtube_probe' | 'youtube' | 'feed_add' | 'feed_refresh'
export type JobStatus = 'queued' | 'running' | 'done' | 'failed'
export type Deliver = 'browser' | 'telegram'
export type YoutubeFormat = 'mp4' | 'mp3'

export interface ToolsInfo {
  analyze_max_urls: number
  /** Худшая цена одной ссылки, $. */
  analyze_price_usd: number
  /** Профиль автора (подписчики) для «Добавленных», $ за автора. */
  profile_price_usd: number
  youtube_max_urls: number
  /** Предел файла YouTube, байт. */
  max_file_bytes: number
  /** Бот включён: в Mini App файлы приходят в чат. */
  telegram: boolean
  budget: { spent_day: number; limit_day: number; spent_month: number; limit_month: number; allowed: boolean } | null
}

/** Итог разбора и задачи feed_add (у неё group_id и group_name — null). */
export interface AnalyzeResult {
  post_ids: number[]
  /** Что легло в «Добавленные»: только рилсы. Разбор без «Сохранить в Залёты» — []; у задач до этапа 7 поля нет. */
  saved_post_ids?: number[]
  group_id: number | null
  group_name: string | null
  failed: { url: string; error: string }[]
  notes: string[]
}

export interface RefreshResult {
  post_id: number
  metrics_at: string | null
  cost_usd: number
  operation?: 'meta_metrics' | 'meta_metrics_refresh' | 'apify_enrich' | 'legacy_refresh'
  metric_view?: 'legacy' | 'meta'
  updated_fields?: string[]
  primary_metrics_updated?: boolean
}

export interface ReelResult {
  post_id: number
  handle: string
  size: number
  /** sent — файл в чате, link — в чате ссылка (файл больше предела бота). */
  sent: 'sent' | 'link' | null
  download_url: string | null
}

export interface YoutubeQuality {
  height: number
  label: string
  fps: number | null
  size: number | null
}

export interface ProbeVideo {
  url: string
  id?: string
  title?: string
  duration?: number | null
  thumbnail?: string | null
  uploader?: string | null
  qualities?: YoutubeQuality[]
  mp3_size?: number | null
  error?: string
  error_code?: string
}

export interface ProbeResult {
  videos: ProbeVideo[]
  max_file_bytes: number
}

export interface YoutubeFile {
  url: string
  title: string | null
  name: string | null
  size: number | null
  sent: 'sent' | 'link' | null
  error: string | null
  download_url: string | null
}

export interface YoutubeResult {
  files: YoutubeFile[]
  format: YoutubeFormat
  deliver: Deliver
}

export interface ToolJob {
  id: string
  kind: JobKind
  status: JobStatus
  title: string
  created_at: string
  updated_at: string
  finished_at: string | null
  queue_position: number | null
  progress: { text: string; done: number; total: number } | null
  error: string | null
  /** budget, apify, collect_off, apify_off, not_found, not_video (у feed_add ни одного рилса). */
  error_code: string | null
  notes: string[]
  /** Коды постов разбора и feed_add: окно из кнопки бота узнаёт по ним свою задачу. */
  codes?: string[] | null
  result: unknown
}

export interface YoutubeItemIn {
  url: string
  height: number | null
  title: string | null
  size: number | null
}

const POLL_MS = 1500

export const toolKeys = {
  info: ['tools', 'info'] as const,
  jobs: ['tools', 'jobs'] as const,
  job: (id: string) => ['tools', 'job', id] as const,
}

export function isActive(job: ToolJob | undefined | null): boolean {
  return job?.status === 'queued' || job?.status === 'running'
}

export function useToolsInfo() {
  return useQuery({
    queryKey: toolKeys.info,
    queryFn: ({ signal }) => request<ToolsInfo>('/api/tools', { signal }),
    staleTime: 60_000,
  })
}

export function useJob(id: string | null) {
  return useQuery({
    queryKey: toolKeys.job(id ?? ''),
    queryFn: ({ signal }) => request<ToolJob>(`/api/tools/jobs/${id}`, { signal }),
    enabled: Boolean(id),
    refetchInterval: (q) => (q.state.data && !isActive(q.state.data) ? false : POLL_MS),
    staleTime: 0,
  })
}

/** Задачи за сутки: список на странице «Инструменты» (без feed_refresh). `enabled` — лента
 *  спрашивает их только в «Добавленных». */
export function useMyJobs({ enabled = true }: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: toolKeys.jobs,
    queryFn: ({ signal }) => request<ToolJob[]>('/api/tools/jobs', { signal }),
    refetchInterval: (q) => (q.state.data?.some(isActive) ? 3000 : false),
    enabled,
  })
}

function useStart<A>(path: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: A) => request<ToolJob>(path, { method: 'POST', body }),
    onSuccess: (job) => {
      qc.setQueryData(toolKeys.job(job.id), job)
      void qc.invalidateQueries({ queryKey: toolKeys.jobs })
    },
  })
}

/** `save` — ещё и в «Добавленные» Залётов. */
export function useStartAnalyze() {
  return useStart<{ urls: string[]; transcribe: boolean; storyboard: boolean; save: boolean }>('/api/tools/analyze')
}

/** Ролики в «Добавленные»: разбор с транскриптом, без группы и кадров. */
export function useStartFeedAdd() {
  return useStart<{ urls: string[] }>('/api/feed/manual')
}

/** «Обновить цифры» добавленного ролика. Уже идёт для этого поста — сервер отдаёт ту же задачу. */
export function useStartRefresh(postId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => request<ToolJob>(`/api/feed/manual/${postId}/refresh`, { method: 'POST' }),
    onSuccess: (job) => qc.setQueryData(toolKeys.job(job.id), job),
  })
}

export function useStartReel() {
  return useStart<{ url: string; deliver: Deliver }>('/api/tools/reels')
}

export function useStartProbe() {
  return useStart<{ urls: string[] }>('/api/tools/youtube/probe')
}

export function useStartYoutube() {
  return useStart<{ items: YoutubeItemIn[]; format: YoutubeFormat; deliver: Deliver }>('/api/tools/youtube')
}

export function startErrorText(detail: string | null | undefined): string {
  switch (detail) {
    case 'no_urls':
      return 'Вставьте хотя бы одну ссылку.'
    case 'bad_urls':
      return 'Одна из строк — не ссылка нужного вида.'
    case 'too_many_urls':
      return 'Слишком много ссылок за раз.'
    case 'too_many_jobs':
      return 'У вас уже 5 задач в работе. Дождитесь, пока какая-нибудь закончится.'
    case 'telegram_off':
      return 'Бот выключен: в чат файл не придёт. Откройте завод в браузере.'
    case 'not_found':
      return 'Ролика уже нет в «Добавленных».'
    case 'network':
      return 'Нет связи с сервером.'
    default:
      return 'Не получилось запустить. Попробуйте ещё раз.'
  }
}
